'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { trackButtonClick } from '@/lib/analytics';

interface PricingTier {
  name: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  cta: string;
  ctaLink: string;
  popular?: boolean;
  priceId?: string;
}

const pricingTiers: PricingTier[] = [
  {
    name: 'Starter',
    price: '$49',
    period: '/month',
    description: 'Perfect for solo contractors and small teams getting started',
    features: [
      'Up to 50 quotes/month',
      'Basic market pricing intelligence',
      'Email quote delivery',
      'Standard templates',
      'Email support',
      'Mobile access',
    ],
    cta: 'Start Free Trial',
    ctaLink: '/free-trial',
    priceId: process.env.NEXT_PUBLIC_STRIPE_PRICE_ID_STARTER || '',
  },
  {
    name: 'Professional',
    price: '$99',
    period: '/month',
    description: 'For growing businesses that need advanced features and insights',
    features: [
      'Unlimited quotes',
      'Advanced pricing intelligence',
      'Custom branding',
      'Priority support',
      'Analytics dashboard',
      'API access',
      'Team collaboration',
      'Advanced reporting',
    ],
    cta: 'Start Free Trial',
    ctaLink: '/free-trial',
    popular: true,
    priceId: process.env.NEXT_PUBLIC_STRIPE_PRICE_ID_PROFESSIONAL || '',
  },
];

export default function PricingCards() {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [isStripeConfigured, setIsStripeConfigured] = useState(true);

  useEffect(() => {
    // Check if Stripe is properly configured
    const checkStripeConfig = () => {
      const publishableKey = process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY || '';
      const hasValidPriceIds = pricingTiers.every(
        (tier) => 
          tier.priceId && 
          tier.priceId.length > 10 &&
          !tier.priceId.includes('your-stripe') && 
          tier.priceId.startsWith('price_')
      );
      const hasValidPublishableKey = 
        publishableKey && 
        publishableKey.length > 20 &&
        !publishableKey.includes('your-stripe') && 
        (publishableKey.startsWith('pk_test_') || publishableKey.startsWith('pk_live_'));
      
      setIsStripeConfigured(hasValidPriceIds && hasValidPublishableKey);
    };

    checkStripeConfig();
  }, []);

  const handleCTAClick = (tierName: string, ctaLink: string) => {
    if (isStripeConfigured) {
      trackButtonClick(`pricing_cta_${tierName.toLowerCase()}`, '/pricing');
    }
  };

  return (
    <section className="section-padding bg-muted/30">
      <div className="container-custom">
        {/* Configuration Warning */}
        {!isStripeConfigured && (
          <div className="max-w-3xl mx-auto mb-8 px-4">
            <div className="bg-warning/10 border border-warning/30 rounded-lg p-4 flex items-start gap-3">
              <Icon name="ExclamationTriangleIcon" size={20} variant="solid" className="text-warning flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-warning mb-1">Payment System Configuration Required</h4>
                <p className="text-sm text-muted-foreground">
                  Stripe payment integration needs to be configured. Please add your Stripe API keys and Price IDs to enable subscriptions.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Billing Toggle */}
        <div className="flex justify-center mb-8 md:mb-12 px-4">
          <div className="inline-flex items-center gap-2 md:gap-4 p-1.5 md:p-2 bg-card border border-border rounded-full w-full sm:w-auto">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`flex-1 sm:flex-none px-4 md:px-6 py-2 rounded-full text-xs md:text-sm font-semibold uppercase tracking-wide transition-all ${
                billingCycle === 'monthly' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`flex-1 sm:flex-none px-4 md:px-6 py-2 rounded-full text-xs md:text-sm font-semibold uppercase tracking-wide transition-all ${
                billingCycle === 'annual' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Annual
              <span className="ml-1 md:ml-2 text-xs bg-success/20 text-success px-1.5 md:px-2 py-0.5 md:py-1 rounded-full">
                Save 20%
              </span>
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid sm:grid-cols-2 gap-6 md:gap-8 max-w-5xl mx-auto px-4">
          {pricingTiers.map((tier, index) => (
            <div
              key={tier.name}
              className={`relative bg-card border-2 rounded-2xl p-6 md:p-8 transition-all hover:shadow-2xl animate-fade-in-blur ${
                tier.popular
                  ? 'border-primary shadow-xl sm:scale-105'
                  : 'border-border hover:border-primary/30'
              }`}
              style={{ animationDelay: `${index * 150}ms` }}
            >
              {tier.popular && (
                <div className="absolute -top-3 md:-top-4 left-1/2 -translate-x-1/2">
                  <span className="inline-block px-3 md:px-4 py-1 bg-primary text-primary-foreground text-xs font-bold uppercase tracking-wider rounded-full">
                    Most Popular
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="text-xl md:text-2xl font-heading font-bold mb-2">{tier.name}</h3>
                <p className="text-xs md:text-sm text-muted-foreground mb-4">{tier.description}</p>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl md:text-5xl font-heading font-bold text-foreground">
                    {billingCycle === 'annual'
                      ? `$${Math.round(parseInt(tier.price.replace('$', '')) * 0.8)}`
                      : tier.price}
                  </span>
                  <span className="text-sm md:text-base text-muted-foreground">{tier.period}</span>
                </div>
                {billingCycle === 'annual' && (
                  <p className="text-xs text-success mt-2">Billed annually - Save 20%</p>
                )}
              </div>

              <ul className="space-y-3 md:space-y-4 mb-6 md:mb-8">
                {tier.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2 md:gap-3">
                    <Icon
                      name="CheckCircleIcon"
                      size={18}
                      variant="solid"
                      className="text-primary flex-shrink-0 mt-0.5 md:w-5 md:h-5"
                    />
                    <span className="text-xs md:text-sm text-foreground">{feature}</span>
                  </li>
                ))}
              </ul>

              {isStripeConfigured ? (
                <Link
                  href={tier.ctaLink}
                  onClick={() => handleCTAClick(tier.name, tier.ctaLink)}
                  className={`block w-full text-center py-3 rounded-full text-sm md:text-base font-semibold uppercase tracking-wide transition-all ${
                    tier.popular
                      ? 'btn-primary' :'border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground'
                  }`}
                >
                  {tier.cta}
                </Link>
              ) : (
                <button
                  disabled
                  className="block w-full text-center py-3 rounded-full text-sm md:text-base font-semibold uppercase tracking-wide bg-muted text-muted-foreground cursor-not-allowed opacity-60"
                  title="Payment system configuration required"
                >
                  Configuration Required
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Trust Elements */}
        <div className="mt-12 md:mt-16 text-center px-4">
          <div className="flex flex-col sm:flex-row flex-wrap justify-center items-center gap-4 sm:gap-6 md:gap-8 text-xs md:text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Icon name="CheckCircleIcon" size={18} variant="solid" className="text-success md:w-5 md:h-5" />
              <span>14-day free trial</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="CheckCircleIcon" size={18} variant="solid" className="text-success md:w-5 md:h-5" />
              <span>No credit card required</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="CheckCircleIcon" size={18} variant="solid" className="text-success md:w-5 md:h-5" />
              <span>Cancel anytime</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}